import React, { useContext } from "react";
import { MapPin, Mail, Phone } from "lucide-react";
import { HashLink as Link } from "react-router-hash-link";
import { AppContext } from "./AppContext";

const Footer = () => {
    const { settings } = useContext(AppContext);

    const { brandName, email, phone, address, linkedin, facebook, instagram } = settings || {};

    const socialLinks = [];
    if (linkedin) socialLinks.push({ label: "LinkedIn", href: linkedin, target: "_blank" });
    if (facebook) socialLinks.push({ label: "Facebook", href: facebook, target: "_blank" });
    if (instagram) socialLinks.push({ label: "Instagram", href: instagram, target: "_blank" });

    const usefulLinks = [
        { label: "Home", href: "/#" },
        { label: "What Founders Actually Say", href: "/#what-founders-say" },
        { label: "How This Works", href: "/#how-this-works" },
        // { label: "Case Studies", href: "/#case-studies" },
        // Contact should point to root plus hash so HashLink navigates from other routes
        { label: "Start a Conversation", href: "/#start-a-conversation" },
    ];

    const legalLinks = [
        { label: "Terms & Conditions", path: "/terms-and-conditions" },
        { label: "Privacy Policy", path: "/privacy-policy" },
        { label: "Refund Policy", path: "/refund-policy" },
    ];

    // WhatsApp floater config
    const WHATSAPP_NUMBER = "91XXXXXDDDDD"; // no +, no spaces
    const WHATSAPP_TEXT = encodeURIComponent("Hi! I found you on the BuzzToBrand website and would like to know more.");

    return (
        <footer className="relative bg-[var(--text-primary)] text-[var(--muted)] px-6 lg:px-20 pt-10 pb-6 overflow-hidden">
            <div className="max-w-7xl mx-auto relative z-10 flex flex-col md:flex-row justify-between gap-10">
                {/* Contact / Brand Info (40%) */}
                <div className="md:w-[40%] flex flex-col items-start gap-2 group cursor-pointer max-w-md">
                    {brandName && (
                        <h2 className="text-[var(--bg-surface)] hover:text-[var(--color-primary)] text-xl font-semibold mb-2 transition-all duration-300">
                            {brandName}
                        </h2>
                    )}

                    {address && (
                        <div className="flex items-start gap-2 text-sm text-[var(--bg-surface)] leading-relaxed">
                            <MapPin className="flex-shrink-0 mt-[2px]" size={18} />
                            <span>{address}</span>
                        </div>
                    )}

                    {email && (
                        <a
                            href={`mailto:${email}`}
                            className="flex items-center gap-2 text-sm text-[var(--bg-surface)] hover:text-[var(--color-primary)] hover:no-underline transition-colors"
                        >
                            <Mail size={18} className="flex-shrink-0" />
                            <span>{email}</span>
                        </a>
                    )}

                    {phone && (
                        <a
                            href={`tel:${phone}`}
                            className="flex items-center gap-2 text-sm text-[var(--bg-surface)] hover:no-underline hover:text-[var(--color-primary)] transition-colors"
                        >
                            <Phone size={16} className="flex-shrink-0" />
                            <span>{phone}</span>
                        </a>
                    )}
                </div>

                {/* Useful Links (20%) */}
                <div className="md:w-[20%] flex flex-col items-start text-left">
                    <h3 className="text-[var(--bg-surface)] text-sm font-semibold mb-2">Useful Links</h3>
                    <ul className="space-y-1">
                        {usefulLinks.map((item) => (
                            <li key={item.label}>
                                {String(item.href).startsWith("http") ? (
                                    <a
                                        href={item.href}
                                        target={item.target || "_blank"}
                                        rel="noopener noreferrer"
                                        className="transition-all text-sm duration-300 hover:no-underline hover:text-[var(--color-primary)] text-[var(--muted)]"
                                    >
                                        {item.label}
                                    </a>
                                ) : (
                                    <Link
                                        to={item.href}
                                        smooth
                                        className="transition-all text-sm duration-300 hover:no-underline hover:text-[var(--color-primary)] text-[var(--muted)]"
                                    >
                                        {item.label}
                                    </Link>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Follow Us (20%) */}
                <div className="md:w-[20%] flex flex-col items-start text-left">
                    <h3 className="text-[var(--bg-surface)] text-sm font-semibold mb-2">Follow Us</h3>
                    <div className="flex flex-col space-y-1">
                        {socialLinks.length > 0 ? (
                            socialLinks.map((s) => (
                                <a
                                    key={s.label}
                                    href={s.href}
                                    target={s.target || "_blank"}
                                    rel={s.target ? "noopener noreferrer" : undefined}
                                    className="transition-all text-sm duration-300 hover:no-underline hover:text-[var(--color-primary)] text-[var(--muted)]"
                                >
                                    {s.label}
                                </a>
                            ))
                        ) : (
                            <p className="text-xs text-[var(--muted)/80]">No social links provided.</p>
                        )}
                    </div>
                </div>

                {/* Legal (20%) */}
                <div className="md:w-[20%] flex flex-col items-start text-left">
                    <h3 className="text-[var(--bg-surface)] text-sm font-semibold mb-2">Legal</h3>
                    <div className="flex flex-col space-y-1">
                        {legalLinks.map((l) => (
                            <Link
                                key={l.label}
                                to={l.path}
                                className="transition-all text-sm duration-300 hover:no-underline hover:text-[var(--color-primary)] text-[var(--muted)]"
                            >
                                {l.label}
                            </Link>
                        ))}
                    </div>
                </div>
            </div>

            {/* Divider + Copyright */}
            <div className="relative border-t border-gray-800 mt-6 pt-4">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/3 h-px bg-gradient-to-r from-transparent via-gray-400 to-transparent opacity-50" />
                <div className="flex items-center justify-center text-sm text-[var(--muted)/80]">
                    <p className="text-xs text-[var(--text-disabled)] text-center">
                        Copyright © 2025 {brandName || ""} - All Rights Reserved.
                    </p>
                </div>
            </div>

            {/* WhatsApp Floater — mobile only 
            <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_TEXT}`}
                target="_blank"
                rel="noopener noreferrer"
                className="fixed bottom-5 right-5 z-50 bg-[#25D366] hover:bg-[#1EBE57] text-white rounded-full p-4 shadow-lg transition-all duration-300 md:hidden"
                aria-label="Chat on WhatsApp"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                >
                    <path d="M12.004 2C6.486 2 2 6.486 2 12.004c0 2.116.668 4.07 1.818 5.678L2 22l4.435-1.773A9.953 9.953 0 0 0 12.004 22C17.523 22 22 17.518 22 12.004S17.523 2 12.004 2zm0 18c-1.676 0-3.23-.49-4.56-1.334l-.327-.205-2.637 1.06.563-2.727-.177-.282A7.935 7.935 0 0 1 4 12.004C4 7.587 7.584 4 12.004 4 16.423 4 20 7.587 20 12.004c0 4.416-3.577 7.996-7.996 7.996zm4.357-5.932c-.242-.121-1.435-.706-1.657-.785-.222-.081-.384-.121-.546.121-.161.242-.626.784-.767.946-.141.162-.283.182-.525.061-.242-.121-1.02-.376-1.943-1.199-.718-.639-1.203-1.426-1.344-1.668-.141-.242-.015-.374.106-.495.108-.108.242-.283.364-.425.121-.141.161-.242.242-.404.081-.162.04-.303-.02-.425-.061-.121-.546-1.314-.749-1.798-.197-.474-.4-.409-.546-.416l-.467-.008a.9.9 0 0 0-.657.303c-.226.243-.862.843-.862 2.055s.884 2.385 1.006 2.547c.121.162 1.739 2.657 4.218 3.725.59.254 1.05.405 1.41.519.592.189 1.13.162 1.556.098.475-.071 1.435-.586 1.64-1.15.202-.566.202-1.051.141-1.151-.061-.101-.22-.162-.464-.283z" />
                </svg>
            </a>
            */}
        </footer>
    );
};

export default Footer;
